/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState } from 'react'
import { useServiceContext } from '../../../root'
import type { DocumentType } from '../../../services/api/api.documenttypes'
import ActionsMenu from '../../../components/shared/ActionsMenu'
import ModalRemove from './Actions/ModalRemove'
import { useNavigate } from 'react-router'

interface TableMenuProps {
  documentType: DocumentType
  onEditDocumentType: (documentType: DocumentType) => void
  onDeleteSuccess?: () => void
  onExportStart?: () => void
  onExportEnd?: () => void
}

const TableMenu: React.FC<TableMenuProps> = ({
  documentType,
  onEditDocumentType,
  onDeleteSuccess,
  onExportStart,
  onExportEnd
}) => {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const { toast } = context.service
  const navigate = useNavigate()
  const [showRemoveModal, setShowRemoveModal] = useState(false)

  const handleEdit = () => {
    onEditDocumentType(documentType)
  }

  const handleRemove = () => {
    setShowRemoveModal(true)
  }

  const handleExport = async () => {
    try {
      // Start loading
      if (onExportStart) {
        onExportStart()
      }

      // Export single document type with entityId parameter
      await context.api.settings.exportSettingsData('DOCUMENT_TYPES', undefined, documentType.id, translate)
    } catch (error) {
      toast.error(translate('SETTINGS_IMPORT_FAIL'))
    } finally {
      // End loading
      if (onExportEnd) {
        onExportEnd()
      }
    }
  }

  const handleActivity = () => {
    // Navigate to settings activity page with properties parameters
    const params = new URLSearchParams({
      id: documentType.id.toString(),
      type: 'DOCUMENT_TYPE_TPL',
      itemName: documentType.name
    })
    navigate(`/settings/activity?${params.toString()}`)
  }

  const settings = {
    menuId: `action-menu-document-type-${documentType.id}`,
    items: [] as Array<{
      value: string
      action: () => void
    }>
  }

  if (!documentType.system) {
    settings.items.push(
      {
        value: 'ACTION_EDIT',
        action: handleEdit
      },
      {
        value: 'ACTION_REMOVE',
        action: handleRemove
      }
    )
  }

  settings.items.push(
    {
      value: 'ACTION_EXPORT',
      action: handleExport
    },
    {
      value: 'BROWSER_ACTION_ACTIVITY',
      action: handleActivity
    }
  )

  return (
    <>
      <ActionsMenu {...settings} />
      {showRemoveModal && (
        <ModalRemove
          documentType={documentType}
          onClose={() => setShowRemoveModal(false)}
          onSuccess={() => {
            setShowRemoveModal(false)
            if (onDeleteSuccess) {
              onDeleteSuccess()
            }
          }}
        />
      )}
    </>
  )
}

export default TableMenu
